// Centralised feature-flag helpers for the agent-first shell rebuild
// (see server/docs/adr/0007-agent-first-shell.md). Each flag has one
// reader so callers don't duplicate the strict `=== 'true'` parse —
// matches the ADR-007 §D-S10 contract (unset / 'false' / '1' / 'yes'
// / 'TRUE' all stay OFF; only the literal string 'true' flips on).
//
// Historical reference: pre-existing flags still live in
// src/zara/config.ts for the Studio Zara preview. This file is for
// shell-wide flags; please don't widen it to studio-specific bits.
//
// M7.1 (#131) owns `isAgentShellV2Enabled` for the AgentPanel mount.
// M7.3 (#132) adds `isAgentV2Route` in the same module so marks,
// nudges, and the surface-context fetch share one predicate. Keep
// the `/v2` prefix stable.

/**
 * AGENT_SHELL_V2_ENABLED — one input to the AgentPanel mount in
 * PipelineShell. Default OFF. The flag is not sufficient: PipelineShell
 * also requires a /v2 pathname (or the empty route allowlist) and a
 * viewport of at least 1280px. The rail chrome is a scaffold and
 * is not design-signed-off. See ADR-007 §D-S1.
 */
export function isAgentShellV2Enabled(): boolean {
  return import.meta.env.VITE_AGENT_SHELL_V2_ENABLED === 'true';
}

/**
 * Explicit /v2 route predicate. Marks, nudges, and the surface-context
 * fetch run only when this returns true. The live catalog at `/catalog`
 * never matches. `/v2/../catalog` normalises to `/catalog` and is rejected.
 */
export function isAgentV2Route(pathname: string): boolean {
  if (typeof pathname !== 'string') return false;
  const trimmed = pathname.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('\\')) return false;
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return false;
  let path: string;
  try {
    const url = new URL(trimmed, 'https://reach-social.invalid');
    if (url.origin !== 'https://reach-social.invalid') return false;
    path = url.pathname;
  } catch {
    return false;
  }
  if (path.split('/').some(part => part === '..' || part === '.')) return false;
  return path === '/v2' || path.startsWith('/v2/');
}
